import { IESDoc } from "../../../app.interface";
import { Table } from "../../../services/pos.service";

// Product Types
export interface Product extends IESDoc {
  id: string;
  name: string;
  sku: string;
  description: string;
  category: string;
  category_id?: string;
  unit: string;
  price: number;
  cost: number;
  stock_quantity: number;
  min_stock_level: number;
  max_stock_level: number;
  is_active: boolean;
  is_composite: boolean;
  is_ingredient: boolean;
  supplier?: string;
  created_by: string;
  store_id?: string;
  is_deleted: boolean;
  created_at: number;
  updated_at: number;
  updated_by: string;
  currentStock: number | null;
  image?: string;
  modifierGroups?: ModifierGroup[];
}

export interface delivery_info {
  name: string;
  phone: string;
  area: string;
  address: string;
}

export interface ProductFilters {
  search?: string;
  category?: string;
  minPrice?: number;
  maxPrice?: number;
  inStock?: boolean;
  outOfStock?: boolean;
  lowStock?: boolean;
  overStock?: boolean;
  pageIndex: number;
  pageSize: number;
  sortOrder?: "asc" | "desc";
}

export interface ProductListResponse {
  items: Product[];
  total: number;
  page: number;
  limit: number;
}

export interface paymentValidateRequest {
  track_id?: string;
}
export interface paymentValidateResponse {
  success?: boolean;
  track_id?: string;
}

export interface paymentLinkRequest {
  amount?: number;
  payment_provider?: string;
}

export interface paymentLinkResponse {
  success?: boolean;
  payment_url?: string;
  track_id?: string;
}

// Order Types
export interface OrderItem {
  item_id?: string;
  id: string;
  name: string;
  price: number;
  quantity: number;
  total_item_cost: number;
  item_profit_margin: number;
  item_revenue: number;
  product_id: string;
  order_id: string;
  created_at: number;
  updated_at: number;
  modifiers: Record<string, string | string[]>;
}

export interface OrderDiscount {
  type: "PERCENTAGE" | "FIXED";
  value: number;
}

export interface PaymentDetails {
  card_type?: string;
  last_four?: string;
  transaction_id?: string;
}

export interface CreateOrderData {
  customer_id?: string;
  waiter_id?: string;
  items: Array<{
    product_id: string | undefined;
    quantity: number;
    price: number;
    modifiers?: Record<string, string | string[]> | {};
  }>;
  payment?: {
    method?: "CASH" | "CARD" | "MOBILE" | "NONE";
    amount: number;
    reference?: string;
  };
}

export interface OrderResponse extends IESDoc {
  id: string;
  order_number: string;
  customer_id?: string;
  session_id: string;
  cashier_id: string;
  status:
    | "on_hold"
    | "pending_payment"
    | "paid"
    | "voided"
    | "refunded"
    | "deleted"
    | "draft"
    | "sent"
    | "served"
    | "completed";
  subtotal: number;
  tax: number;
  total: number;
  payment_method?: string;
  payment_status?: string;
  transaction_id?: string;
  order_lines: OrderItem[];
  created_at: number;
  updated_at: number;
  table: Table;
  orderType: string;
  delivery_info?: delivery_info | {};
}

export interface CreateOrderResponse {
  order: OrderResponse;
  order_lines: OrderItem[];
  account_entry: any; // TODO: Define AccountEntry type
  change: number;
}

export interface RefundOrderData {
  id: string;
  items?: Array<{
    line_item_id: string;
    quantity: number;
  }>;
  reason: string;
  refund_payment: boolean;
}

export interface RefundResponse {
  id: string;
  original_order_id: string;
  refunded_items: Array<{
    product_id: string;
    quantity: number;
    amount: number;
  }>;
  total_refund_amount: number;
  created_at: number;
  created_by: string;
}

// Stock Movement Types
export interface StockMovement extends IESDoc {
  id: string;
  product_id: string;
  quantity: number;
  type: "IN" | "OUT";
  reference: string;
  notes?: string;
  created_by: string;
  created_at: number;
  updated_at: number;
}

export interface CreateStockMovementData {
  product_id: string;
  quantity: number;
  movement_type: "IN" | "OUT" | "TRANSFER";
  source_location?: string;
  destination_location?: string;
  notes?: string;
  movement_date?: number;
}

export interface StockMovementFilters {
  product_id: string;
  start_date?: number;
  end_date?: number;
  page: number;
  limit: number;
}

export interface StockMovementListResponse {
  items: StockMovement[];
  total: number;
  page: number;
  limit: number;
}

// Cart Types
export interface CartItem {
  id: string; // Unique ID per variation
  product_id: string;
  name: string;
  basePrice: number;
  price: number; // Base + modifier impact
  quantity: number;
  modifiers: Record<string, string | string[]>; // Raw modifier selection

  modifierDetails: Array<{
    id: string;
    name: string;
    price: number;
  }>; // Detailed breakdown
}

export interface CartProps {
  items: CartItem[];
  onRemoveItem: (productId: string) => void;
  onUpdateQuantity: (productId: string, quantity: number) => void;
  onCheckout: () => void;
  subtotal: number;
  tax: number;
  total: number;
  onHoldOrder: () => Promise<void>;
  onCancelOrder: () => void;
  isEditing?: boolean;
  editingOrder?: OrderResponse;
}

export interface OrderData {
  id: string;
  items: OrderItem[];
  payment: {
    method: string;
    amount: number;
    reference?: string;
    change?: number;
  };
  status: string;
  created_at: number;
}

export interface PaymentData {
  method: "cash" | "card" | "mobile";
  amount: number;
  reference?: string;
  change?: number;
}

export interface Cart {
  items: CartItem[];
  subtotal: number;
  tax: number;
  total: number;
}

export interface ProductListProps {
  products: Product[];
  loading: boolean;
  onProductSelect: (product: Product) => void;
}

export interface APIResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

export interface SessionResponse extends IESDoc {
  id: string;
  cashier_id: string;
  store_id: string;
  start_time: number;
  end_time?: number;
  status: "active" | "ended";
  created_at: number;
  updated_at: number;
}

// Session Types
export interface SessionDenominations {
  bills: Array<{ value: number; count: number }>;
  coins: Array<{ value: number; count: number }>;
}

export interface StoreLocation {
  lat: number;
  lon: number;
}

export interface StartSessionData {
  cashier_id: string;
  store_id: string;
}

export interface EndSessionData {
  session_id: string;
  notes?: string;
}

export interface Modifier {
  id: string;
  name: string;
  price: number;
}

export interface ModifierGroup {
  id: string;
  name: string;
  type: "radio" | "checkbox";
  required: boolean;
  modifiers: Modifier[];
  min_selection: number;
  max_selection: number;
}

export interface OrderDiscount {
  type: "PERCENTAGE" | "FIXED";
  value: number;
  reason?: string; // required if over threshold
}

export interface DiscountThresholdForm {
  cashierMaxPercentageDiscount: number;
  cashierMaxFixedDiscount: number;
}

export interface DiscountThreshold {
  cashierMaxPercentageDiscount: number;
  cashierMaxFixedDiscount: number;
}

//this is a place holder til we get central currency system
export const CURRENCY = "BHD";
